import dayjs from "dayjs";
import { fetchFromBusinessCentral } from "../utils/businessCentralUtils";

// Builders
export function buildSyncMessage(invoiceCount, creditMemoCount, postedInvoiceCount, postedCreditMemosCount) {
    if (invoiceCount + creditMemoCount + postedInvoiceCount + postedCreditMemosCount === 0) {
        return "No se encontraron documentos históricos para los filtros seleccionados.";
    }

    let parts = [];

    if (invoiceCount > 0) {
        parts.push(
            invoiceCount === 1
                ? "1 factura fue sincronizada correctamente"
                : `${invoiceCount} facturas fueron sincronizadas correctamente`
        );
    }

    if (creditMemoCount > 0) {
        parts.push(
            creditMemoCount === 1
                ? "1 nota de crédito fue sincronizada correctamente"
                : `${creditMemoCount} notas de crédito fueron sincronizadas correctamente`
        );
    }

    if (postedInvoiceCount > 0) {
        parts.push(
            postedInvoiceCount === 1
                ? "1 factura contabilizada fue sincronizada correctamente"
                : `${postedInvoiceCount} facturas contabilizadas fueron sincronizadas correctamente`
        );
    }

    if (postedCreditMemosCount > 0) {
        parts.push(
            postedCreditMemosCount === 1
                ? "1 nota de crédito contabilizada fue sincronizada correctamente"
                : `${postedCreditMemosCount} notas de crédito contabilizadas fueron sincronizadas correctamente`
        );
    }

    return parts.join(" · ");
}
export function buildFilters(values, isPosted) {
    const fechaInicio = dayjs(values.FechaInicio).format("YYYY-MM-DD");
    const fechaFin = dayjs(values.FechaFin).format("YYYY-MM-DD");

    const filters = [
        `Tienda eq '${values.TPV}'`,
        `${isPosted ? "TPV" : "Caja"} eq '${values.Caja}'`,
        `(Posting_Date ge ${fechaInicio} and Posting_Date le ${fechaFin})`
    ];

    if (values.Turno) {  // Turno es el unico filtro opcional
        filters.push(`Turno eq ${values.Turno}`);
    }

    return `$filter=${filters.join(" and ")}`;
}

// Mapping: API → IndexedDB schema
export const mapInvoice = (apiObj) => {
    return {
        No: apiObj.No ?? "",
        Document_Type: apiObj.Document_Type ?? "",
        Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
        Posting_No: apiObj.External_Document_No ?? "",
        draftNo: "",
        Colegio: apiObj.Cod_Colegio ?? "",
        Bill_to_Name: apiObj.Bill_to_Name ?? "",
        Bill_to_Address: apiObj.Bill_to_Address ?? "",
        Bill_to_City: apiObj.Bill_to_City ?? "",
        VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
        Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
        Ship_to_Code: apiObj.Ship_to_Code ?? "",
        Tipo_Documento_SrI: apiObj.Tipo_Documento_SrI ?? "",
        E_Mail: apiObj.SellToEmail ?? apiObj.BillToContactEmail ?? "",
        Telefono: apiObj.Sell_to_Phone ?? "",
        Mobil: apiObj.SellToMobilePhoneNo ?? "",
        caja: apiObj.Caja ?? "",
        clientLocalId: "",
        turno: apiObj.Turno ?? null,
        Dto: "",
        importeDto: "",
        Campaign_No_: apiObj.Campaign_No_ ?? apiObj.Campaign_No ?? "",
        Cupon: apiObj.Cupon ?? "",
        Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
        No_Detalle: apiObj.No_Detalle ?? "",
        Payment_Method: apiObj.Payment_Method_Code ?? "",
        Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
        Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
        No_Comprobante_Fiscal_Rel: apiObj.No_Comprobante_Fiscal_Rel ?? "",
        systemId: apiObj.systemId ?? "",
        pendingSync: 0,
        Posting_Date: apiObj.Posting_Date ?? "",
        lastModified: apiObj.Posting_Date ?? "",
        lastSynced: new Date().toISOString(),
        deleted: 0,
        syncing: 0,
        syncStartTime: null,
        posted: 0
    };
};
export const mapPostedInvoice = (apiObj, documentType) => {
    return {
        No: apiObj.No ?? "",
        Document_Type: documentType,
        Sell_to_Customer_No_: apiObj.Sell_to_Customer_No ?? "",
        Posting_No: apiObj.External_Document_No ?? "",
        draftNo: "",
        Colegio: apiObj.Cod_Colegio ?? "",
        Bill_to_Name: apiObj.Bill_to_Name ?? "",
        Bill_to_Address: apiObj.Bill_to_Address ?? "",
        Bill_to_City: apiObj.Bill_to_City ?? "",
        VAT_Registration_No_: apiObj.VAT_Registration_No ?? "",
        Bill_to_Country_Region_Code: apiObj.Bill_to_Country_Region_Code ?? "",
        Ship_to_Code: apiObj.Ship_to_Code ?? "",
        Tipo_Documento_SrI: apiObj.Tipo_Documento ?? "",
        E_Mail: apiObj.SellToEmail || apiObj.BillToContactEmail || apiObj.E_Mail || "",
        Telefono: apiObj.Sell_to_Phone ?? "",
        Mobil: apiObj.SellToMobilePhoneNo ?? "",
        caja: apiObj.Caja ?? apiObj.TPV ?? "",
        clientLocalId: "",
        turno: apiObj.Turno ?? null,
        Dto: "",
        importeDto: "",
        Cupon: apiObj.Cupon ?? "",
        Tipo_Detalle: apiObj.Tipo_Detalle ?? "",
        No_Detalle: apiObj.No_Detalle ?? "",
        Payment_Method: apiObj.Payment_Method_Code ?? "",
        Pre_Assigned_No: apiObj.Pre_Assigned_No ?? "",
        Establecimiento_Factura: apiObj.Establecimiento_Factura ?? "",
        Punto_de_Emision_Factura: apiObj.Punto_de_Emision_Factura ?? "",
        pendingSync: 0,
        Posting_Date: apiObj.Posting_Date ?? apiObj.Document_Date ?? "",
        lastModified: apiObj.Posting_Date ?? apiObj.Document_Date ?? "",
        lastSynced: new Date().toISOString(),
        deleted: 0,
        syncing: 0,
        syncStartTime: null,
        posted: 1,
        Campaign_No_: apiObj.Campaign_No ?? apiObj.Campaign_No_
    };
};

// Mapping: invoices lines + credit memos lines + posted invoice lines
export const mapLine = (apiResponse) => {
    const lineAmount = apiResponse.Line_Amount ?? 0;
    const discountAmount = apiResponse.Line_Discount_Amount ?? 0;
    const totalVatAmount = apiResponse.Total_VAT_Amount ?? 0;

    // Calcular porcentaje de descuento
    let discountPercent =
        lineAmount + discountAmount > 0
            ? (discountAmount * 100) / (lineAmount + discountAmount)
            : 0;

    // Calcular porcentaje de IVA si no viene
    let calculatedPorIva =
        lineAmount > 0 ? (totalVatAmount * 100) / lineAmount : 0;

    return {
        invoiceNo: apiResponse.Document_No,
        Line_No: apiResponse.Line_No,
        Type: apiResponse.Type,
        No: apiResponse.No,
        Description: apiResponse.Description,
        Quantity: apiResponse.Quantity,
        Unit_Price: apiResponse.Unit_Price,
        Line_Discount_: discountPercent,
        Line_Amount: lineAmount,
        PorIva:
            apiResponse.Retention_VAT_Percent ??
            apiResponse.VAT_Percent ??
            calculatedPorIva,
        Total_VAT_Amount: totalVatAmount,
        pendingSync: 0,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        deleted: 0,
    };
};

// Mapping: posted credit memos y facturas sin contabilizar
export const mapLineCalculateVat = (apiResponse) => {
    const lineAmount = apiResponse.Line_Amount ?? 0; // sin IVA
    const discountAmount = apiResponse.Line_Discount_Amount ?? 0;
    const amountIncludingVat = apiResponse.Amount_Including_VAT ?? 0;

    // Calcular porcentaje de descuento real (si Line_Amount ya está descontado)
    let discountPercent =
        lineAmount + discountAmount > 0
            ? (discountAmount * 100) / (lineAmount + discountAmount)
            : 0;

    // Calcular IVA solo si existe monto de IVA (> 0)
    let calculatedPorIva = lineAmount > 0 ? ((amountIncludingVat - lineAmount) * 100) / lineAmount : 0;

    let result = {
        invoiceNo: apiResponse.Document_No,
        Line_No: apiResponse.Line_No,
        Type: apiResponse.Type,
        No: apiResponse.No,
        Description: apiResponse.Description,
        Quantity: apiResponse.Quantity,
        Unit_Price: apiResponse.Unit_Price, // precio base sin IVA
        Line_Discount_: discountPercent, // % de descuento (lo que tu cálculo espera)
        Line_Amount: lineAmount, // sin IVA
        PorIva: calculatedPorIva,
        pendingSync: 0,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        deleted: 0,
    };

    return result;
};

//Mapping: Payment details invoice + payment details credit memos
export function mapPaymentDetails(bcObj) {
    const amount = parseFloat(bcObj?.Importe ?? 0);
    const discount = parseFloat(bcObj?.Descuento ?? 0);

    return {
        invoiceId: 0, // No existe en BC 
        Document_Type: bcObj?.Document_Type || "Invoice",
        Document_No_: bcObj?.Document_No_ || "",
        Line_No_: bcObj?.Line_No_ || 1,
        Payment_Method: bcObj?.Forma_de_Pago || "EFECTIVO",
        Amount: amount,
        Dto: discount,
        Dto_Amount: parseFloat((amount * discount) / 100) || 0,
        deleted: false,
        lastModified: new Date().toISOString(),
        lastSynced: new Date().toISOString(),
        pendingSync: 0
    };
}

// Chunk logic -> crea lotes de documentos para hacer multiples llamadas (Una sola = Network error)
export function chunkArray(array, size) {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
        result.push(array.slice(i, i + size));
    }
    return result;
}

export async function fetchLinesInChunks(
    endpoint,
    documents,
    mapFn,
    chunkSize = 35,
    filterDocumentLabel = 'Document_No',
    usePreAssigned = false) {
    const chunks = chunkArray(documents, chunkSize);
    let allResults = [];

    for (const chunk of chunks) {
        const filter = chunk
            .map((doc) => {
                const value = usePreAssigned ? doc.Pre_Assigned_No : doc.No;
                return `${filterDocumentLabel} eq '${value}'`;
            })
            .join(" or ");

        const response = await fetchFromBusinessCentral(
            `${endpoint}?$filter=${filter}`
        );

        allResults.push(...response.map(mapFn));
    }

    return allResults;
}

// Mapea respuestas en formato que espera el componente Select de Antd
export const mapApiOptionsResponse = (apiResponse) => {
    if (!apiResponse || !Array.isArray(apiResponse)) {
        return [];
    }

    return apiResponse.map(item => {
        // Detectar el campo de ID según la API
        const id =
            item["Cód_Departamento_FE"] ||
            item["Cód_Distrito_FE"] ||
            item["Cód_Ciudad_FE"] ||
            item["Code"] ||
            null;

        // Detectar el campo de descripción o nombre
        const label =
            item["Descripción"] ||
            item["Name"] ||
            "";

        return {
            label,
            value: id,
        };
    });
}

// Función para emitir eventos de sincronización
export const emitSyncEvent = (message, isComplete = false) => {
    const event = new CustomEvent('syncStatus', {
        detail: { message, isComplete }
    });
    window.dispatchEvent(event);
};
